import {Component, Input} from '@angular/core';
import {EventsService} from '../../../../../../../../../../@shared/services/events-service';
import {_AQH_EVE_2, _AQH_EVE_5, AQH_EVE_4} from '../../../../../../../../../../@shared/models/input.module';
import {
  CompanyResponse,
  EventResponse,
  EventTypeResponse
} from '../../../../../../../../../../@shared/models/response.module';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-edit-event',
  standalone: false,
  templateUrl: './edit-event.html',
  styleUrl: './edit-event.css'
})
export class EditEvent {
  @Input() company!: CompanyResponse;
  @Input() event!: EventResponse;

  eventTypes: EventTypeResponse[] = [];

  event_name = '';
  event_description = '';
  event_type_id!: EventTypeResponse | null;
  start_date: string | null = null;
  end_date: string | null = null;
  address = '';
  postal_code = '';
  city = '';
  country = '';
  registration_deadline: string | null = null;

  logo?: File;
  header?: File;

  saving = false;

  constructor(private eventService: EventsService,
              private modal: NgbActiveModal) {}

  ngOnInit() {
    this.getEventTypes();
  }

  private getEventTypes() {
    const params: AQH_EVE_4 = {};
    this.eventService.getEventTypes(params).subscribe((res) => {
      this.eventTypes = res || [];
      this.patchFromEvent();
    });
  }

  private toInputLocal(dt: Date | string | null | undefined): string {
    if (!dt) return '';

    // Already correct format
    if (typeof dt === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dt)) {
      return dt;
    }

    // 1) Handle "YYYY-MM-DD HH:mm[:ss]"
    if (typeof dt === 'string') {
      const s = dt.trim();

      const ymd = s.replace('T', ' ').match(
        /^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2})(?::(\d{2}))?$/
      );
      if (ymd) {
        const [, yyyy, mm, dd, hh, mi] = ymd;
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      // 2) Handle "dd/mm/yyyy, HH:mm"
      const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})$/);
      if (dmy) {
        let [, dd, mm, yyyy, hh, mi] = dmy;
        dd = dd.padStart(2, '0');
        mm = mm.padStart(2, '0');
        hh = hh.padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      // 3) Handle "mm/dd/yyyy, HH:mm" (US ordering)
      const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
      if (mdy) {
        let [, mm, dd, yyyy, hh, mi, ampm] = mdy;
        let hour = parseInt(hh, 10);
        if (ampm) {
          const isPM = ampm.toUpperCase() === 'PM';
          if (isPM && hour < 12) hour += 12;
          if (!isPM && hour === 12) hour = 0;
        }
        dd = dd.padStart(2, '0');
        mm = mm.padStart(2, '0');
        const hh2 = String(hour).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh2}:${mi}`;
      }

      // 4) Last resort: native Date parse
      const nd = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
      if (!isNaN(nd.getTime())) {
        const yyyy = nd.getFullYear();
        const mm = String(nd.getMonth() + 1).padStart(2, '0');
        const dd = String(nd.getDate()).padStart(2, '0');
        const hh = String(nd.getHours()).padStart(2, '0');
        const mi = String(nd.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
      }

      return '';
    }

    // Date instance (already local): format without TZ conversion
    const d = dt as Date;
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }

  private patchFromEvent() {
    if (!this.event) return;

    this.event_name = this.event.event_name || '';
    this.event_description = this.event.event_description || '';
    this.address = this.event.address || '';
    this.postal_code = this.event.postal_code || '';
    this.city = this.event.city || '';
    this.country = this.event.country || '';

    this.start_date = this.toInputLocal(this.event.start_date as any);
    this.end_date = this.toInputLocal(this.event.end_date as any);
    this.registration_deadline = this.toInputLocal(this.event.registration_deadline as any);

    this.event_type_id =
      this.eventTypes.find(t => t.event_type_id === this.event.event_type_id) || null;
  }

  save() {
    if (!this.event?.event_id || !this.event_type_id) return;
    this.saving = true;

    const params: _AQH_EVE_2 = {
      event_id: this.event.event_id,
      event_name: this.event_name,
      event_description: this.event_description,
      event_type_id: this.event_type_id.event_type_id,
      start_date: this.start_date || '',
      end_date: this.end_date || '',
      address: this.address,
      postal_code: this.postal_code,
      city: this.city,
      country: this.country,
      registration_deadline: this.registration_deadline || '',
    };

    this.eventService.editEvent(params).subscribe((res) => {
      const updated = res.data;

      const uploads: Array<Promise<any>> = [];

      if (this.logo) {
        const logoParams: _AQH_EVE_5 = {
          event_id: updated.event_id,
          image_type: 'logo',
          image: this.logo
        };
        uploads.push(
          this.eventService.editEventFiles(logoParams).toPromise()
        );
      }

      if (this.header) {
        const headerParams: _AQH_EVE_5 = {
          event_id: updated.event_id,
          image_type: 'header',
          image: this.header
        };
        uploads.push(
          this.eventService.editEventFiles(headerParams).toPromise()
        );
      }

      if (uploads.length) {
        Promise.allSettled(uploads).finally(() => {
          this.saving = false
          this.modal.close(true);
        });
      } else {
        this.saving = false;
        this.modal.close(true);
      }
    }, () => (this.saving = false));
  }

  close() {
    this.modal.close(false)
  }
}
